import { AudioLines, SlidersHorizontal } from 'lucide-react';
import type { AudioTrackState } from '../../types/audio';

interface MixerPanelProps {
  tracks: AudioTrackState[];
  getLevel: (trackId: string) => number;
  onVolume: (trackId: string, volume: number) => void;
  onMuted: (trackId: string, muted: boolean) => void;
  onSolo: (trackId: string, solo: boolean) => void;
  onPan: (trackId: string, pan: number) => void;
}

const roleColors = { music: '#60a5fa', click: '#fbbf24', guide: '#f97316' };
const roleNames = { music: 'Música', click: 'Clic', guide: 'Guía' };

export function MixerPanel({
  tracks,
  getLevel,
  onVolume,
  onMuted,
  onSolo,
  onPan,
}: MixerPanelProps) {
  return (
    <section className="min-h-0 flex-1 overflow-auto bg-[#090d12] p-5 lg:p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-cyan-400" />
          <h2 className="text-sm font-semibold">Mezcladora</h2>
        </div>
        <p className="text-xs text-slate-600">
          {tracks.length} canal{tracks.length === 1 ? '' : 'es'}
        </p>
      </div>
      {tracks.length ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {tracks.map((track) => (
            <ChannelStrip
              key={track.id}
              track={track}
              level={getLevel(track.id)}
              onVolume={onVolume}
              onMuted={onMuted}
              onSolo={onSolo}
              onPan={onPan}
            />
          ))}
        </div>
      ) : (
        <div className="flex min-h-48 items-center justify-center rounded-xl border border-dashed border-white/10 text-center text-sm text-slate-500">
          La mezcladora aparecerá al cargar pistas WAV.
        </div>
      )}
    </section>
  );
}

function ChannelStrip({
  track,
  level,
  onVolume,
  onMuted,
  onSolo,
  onPan,
}: {
  track: AudioTrackState;
  level: number;
  onVolume: MixerPanelProps['onVolume'];
  onMuted: MixerPanelProps['onMuted'];
  onSolo: MixerPanelProps['onSolo'];
  onPan: MixerPanelProps['onPan'];
}) {
  const activeBars = Math.round(level * 12);
  return (
    <article
      className="channel-strip"
      style={{ '--track-color': roleColors[track.role] } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-200">{track.name}</p>
          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
            {roleNames[track.role]}
          </p>
        </div>
        <AudioLines size={15} className="shrink-0 text-[var(--track-color)]" />
      </div>
      <div className="mt-4 flex h-24 items-end gap-1" aria-label={`Nivel de ${track.name}`}>
        {Array.from({ length: 12 }).map((_, index) => (
          <span
            key={index}
            className={index < activeBars ? 'meter-bar-active' : 'meter-bar'}
            style={{ height: `${30 + ((index * 17) % 65)}%` }}
          />
        ))}
      </div>
      <label className="mt-5 block">
        <span className="sr-only">Volumen de {track.name}</span>
        <input
          className="volume-slider"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={track.volume}
          onChange={(event) => onVolume(track.id, Number(event.target.value))}
        />
      </label>
      <p className="mt-1 text-center font-mono text-[10px] text-slate-500">
        {Math.round(track.volume * 100)}%
      </p>
      <label className="mt-3 block text-center text-[10px] text-slate-600">
        PAN {track.pan.toFixed(1)}
        <input
          className="pan-slider mt-1"
          type="range"
          min="-1"
          max="1"
          step="0.1"
          value={track.pan}
          onChange={(event) => onPan(track.id, Number(event.target.value))}
          aria-label={`Paneo de ${track.name}`}
        />
      </label>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          className={`channel-button ${track.muted ? 'channel-button-mute' : ''}`}
          type="button"
          onClick={() => onMuted(track.id, !track.muted)}
          aria-pressed={track.muted}
        >
          M
        </button>
        <button
          className={`channel-button ${track.solo ? 'channel-button-solo' : ''}`}
          type="button"
          onClick={() => onSolo(track.id, !track.solo)}
          aria-pressed={track.solo}
        >
          S
        </button>
      </div>
    </article>
  );
}
